import { useState } from 'react';
import { DeviceControllerIcon, SearchIcon, SparkleIcon, VideoControllerIcon } from '@/components/icons';
import type { UserRole } from '@/types/common';
import type { FlowFieldOption, FlowFieldSchema, FlowStepSchema } from '@/flows/types';
import { resolveRoleText } from '@/flows/types';
import { FieldLabelRow } from './FieldLabelRow';

type DraftValue = string | number | null | undefined | string[];

interface FormRendererProps<TDraft> {
  schema: FlowStepSchema<TDraft>;
  role: UserRole;
  draft: TDraft;
  aiFilledFields?: string[];
  showAiBanner?: boolean;
  onDraftChange: (patch: Partial<TDraft>) => void;
  onBack: () => void;
  onNext: () => void;
  disableNext?: boolean;
  disabled?: boolean;
  /** Hides the primary footer action when confirm is handled in the intro row. */
  hidePrimaryAction?: boolean;
  /** Hides footer actions (e.g. Confirm) while the flow is saving. */
  saving?: boolean;
}

function toStringValue(value: DraftValue): string {
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return '';
}

function resolveOptions<TDraft>(
  field: FlowFieldSchema<TDraft>,
  draft: TDraft
): FlowFieldOption[] {
  if (!field.options) return [];
  return typeof field.options === 'function' ? field.options(draft) : field.options;
}

function ListSingleOptionIcon({ selected }: { selected: boolean }) {
  return <DeviceControllerIcon selected={selected} size={33} />;
}

export function FormRenderer<TDraft>({
  schema,
  role,
  draft,
  aiFilledFields = [],
  showAiBanner = false,
  onDraftChange,
  onBack,
  onNext,
  disableNext = false,
  disabled = false,
  hidePrimaryAction = false,
  saving = false,
}: FormRendererProps<TDraft>) {
  const visibleFields = schema.fields.filter((field) => (field.visibleWhen ? field.visibleWhen(draft) : true));
  const instruction = resolveRoleText(schema.instruction, role);
  const isDualColumn = schema.layout === 'dual-column' && visibleFields.length >= 2;
  const isCredentialLayout = schema.layout === 'credential';
  const isStackLayout = schema.layout === 'stack';
  const credentialTabsField = isCredentialLayout
    ? visibleFields.find((field) => field.type === 'segmented')
    : undefined;
  const credentialGridFields = isCredentialLayout
    ? visibleFields.filter((field) => field !== credentialTabsField)
    : visibleFields;

  const applyChange = (field: FlowFieldSchema<TDraft>, value: string) => {
    if (field.derivePatch) {
      onDraftChange(field.derivePatch(value, draft));
      return;
    }
    onDraftChange({ [field.id]: value } as Partial<TDraft>);
  };

  const renderField = (field: FlowFieldSchema<TDraft>) => {
    const labelText = resolveRoleText(field.label, role) ?? field.id;
    const value = (draft as Record<string, DraftValue>)[field.id];
    const stringValue = toStringValue(value);
    const options = resolveOptions(field, draft);
    if (field.type === 'card-picker') {
      return (
        <section key={field.id} className="option-cards" aria-labelledby={`${field.id}-heading`}>
          {field.heading ? (
            <h3 id={`${field.id}-heading`} className="option-cards__heading">
              {field.heading}
            </h3>
          ) : null}
          {field.subheading ? <p className="option-cards__sub">{field.subheading}</p> : null}
          {options.map((option) => {
            const isSelected = stringValue === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={`option-card${isSelected ? ' option-card--selected' : ''}`}
                onClick={() => applyChange(field, option.value)}
              >
                <span className="option-card__icon" aria-hidden>
                  {option.value === 'door-controller' ? (
                    <DeviceControllerIcon selected={isSelected} size={33} />
                  ) : option.value === 'video-recorder' ? (
                    <VideoControllerIcon selected={isSelected} size={33} />
                  ) : null}
                </span>
                <div className="option-card__content">
                  <p className="option-card__title">{option.label}</p>
                  {option.description ? (
                    <p className="option-card__desc">{option.description}</p>
                  ) : null}
                </div>
              </button>
            );
          })}
        </section>
      );
    }

    if (field.type === 'segmented') {
      return (
        <div key={field.id} className="credential-tabs" role="tablist" aria-label={labelText}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`credential-tabs__tab${stringValue === option.value ? ' credential-tabs__tab--active' : ''}`}
              onClick={() => applyChange(field, option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      );
    }

    if (field.type === 'list-single') {
      const isModelPicker = field.listIcon === 'device-controller';
      return (
        <div
          key={field.id}
          className={`form-kit__column${isModelPicker ? ' form-kit__column--model' : ' form-kit__column--manufacturer'}`}
        >
          <p className="form-kit__label form-kit__label--picker">{labelText}</p>
          <div
            className={`form-kit__list-panel${isModelPicker ? ' form-kit__list-panel--model' : ' form-kit__list-panel--manufacturer'}`}
          >
            <div
              className="form-kit__list"
              role="listbox"
              aria-label={labelText}
            >
              {options.length > 0 ? (
                options.map((option) => {
                  const isSelected = stringValue === option.value;
                  return (
                    <button
                      key={`${field.id}-${option.value}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`form-kit__list-item${isSelected ? ' form-kit__list-item--selected' : ''}${isModelPicker ? ' form-kit__list-item--model' : ' form-kit__list-item--plain'}`}
                      onClick={() => applyChange(field, option.value)}
                    >
                      {isModelPicker ? (
                        <span className="form-kit__list-icon" aria-hidden>
                          <ListSingleOptionIcon selected={isSelected} />
                        </span>
                      ) : null}
                      <span className="form-kit__list-label">{option.label}</span>
                    </button>
                  );
                })
              ) : (
                <p className="form-kit__empty-hint">No options available</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (field.type === 'list-multi') {
      return (
        <ListMultiField
          key={field.id}
          fieldId={field.id}
          labelText={labelText}
          options={options}
          values={Array.isArray(value) ? value : []}
          searchable={field.searchable}
          searchPlaceholder={field.searchPlaceholder}
          disabled={disabled}
          aiField={field.aiField}
          aiFilledFields={aiFilledFields}
          onDraftChange={onDraftChange}
        />
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.id} className="form-kit__field">
          <FieldLabelRow
            htmlFor={field.id}
            required={field.required}
            aiField={field.aiField}
            aiFilledFields={aiFilledFields}
          >
            {labelText}
          </FieldLabelRow>
          <select
            id={field.id}
            className="form-kit__select"
            value={stringValue}
            onChange={(event) => applyChange(field, event.target.value)}
            disabled={disabled}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    const inputType =
      field.type === 'email' ||
      field.type === 'tel' ||
      field.type === 'password' ||
      field.type === 'number' ||
      field.type === 'date'
        ? field.type
        : 'text';

    return (
      <div key={field.id} className="form-kit__field">
        <FieldLabelRow
          htmlFor={field.id}
          required={field.required}
          aiField={field.aiField}
          aiFilledFields={aiFilledFields}
        >
          {labelText}
        </FieldLabelRow>
        <input
          id={field.id}
          type={inputType}
          className="form-kit__input"
          value={stringValue}
          onChange={(event) => applyChange(field, event.target.value)}
          placeholder={field.placeholder}
          spellCheck={false}
          autoComplete="off"
          disabled={disabled}
        />
      </div>
    );
  };

  return (
    <div className="form-kit">
      {!showAiBanner && instruction ? <p className="form-kit__instruction">{instruction}</p> : null}

      {isCredentialLayout ? (
        <div className="form-kit__credential-layout">
          {credentialTabsField ? (
            <div className="form-kit__credential-tabs-row">{renderField(credentialTabsField)}</div>
          ) : null}
          {credentialGridFields.length > 0 ? (
            <div className="form-kit__grid form-kit__grid--credential">
              {credentialGridFields.map(renderField)}
            </div>
          ) : null}
        </div>
      ) : isStackLayout ? (
        <div className="form-kit__stack">{visibleFields.map(renderField)}</div>
      ) : isDualColumn ? (
        <div className="form-kit__dual-columns">
          {visibleFields.slice(0, 2).map(renderField)}
        </div>
      ) : (
        <div className="form-kit__grid">{visibleFields.map(renderField)}</div>
      )}

      {!saving ? (
        <div className="form-kit__actions">
          <button
            type="button"
            className="form-kit__btn"
            onClick={onBack}
            disabled={disabled}
          >
            {schema.backLabel ?? 'Back'}
          </button>
          {!hidePrimaryAction ? (
            <button
              type="button"
              className="form-kit__btn form-kit__btn--primary"
              onClick={onNext}
              disabled={disabled || disableNext}
            >
              {schema.nextLabel ?? 'Next'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ListMultiCheckbox({ checked }: { checked: boolean }) {
  return (
    <span className="form-kit__list-checkbox" aria-hidden>
      {checked ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect width="20" height="20" rx="4" fill="var(--color-primary)" />
          <path
            d="M6 10l3 3 5-5"
            stroke="var(--color-text-inverse)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="0.5" y="0.5" width="19" height="19" rx="3.5" stroke="var(--color-border)" />
        </svg>
      )}
    </span>
  );
}

function ListMultiField<TDraft>({
  fieldId,
  labelText,
  options,
  values,
  searchable,
  searchPlaceholder = 'Search',
  disabled,
  aiField,
  aiFilledFields,
  onDraftChange,
}: {
  fieldId: string;
  labelText: string;
  options: FlowFieldOption[];
  values: string[];
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  aiField?: string;
  aiFilledFields: string[];
  onDraftChange: (patch: Partial<TDraft>) => void;
}) {
  const [query, setQuery] = useState('');
  const normalized = query.trim().toLowerCase();
  const filtered =
    searchable && normalized
      ? options.filter(
          (option) =>
            option.label.toLowerCase().includes(normalized) ||
            (option.description?.toLowerCase().includes(normalized) ?? false)
        )
      : options;

  const filteredValues = filtered.map((option) => option.value);
  const allFilteredSelected =
    filteredValues.length > 0 && filteredValues.every((value) => values.includes(value));

  const setValues = (next: string[]) => {
    onDraftChange({ [fieldId]: next } as Partial<TDraft>);
  };

  const toggleOption = (optionValue: string, checked: boolean) => {
    if (checked) {
      setValues([...values, optionValue]);
      return;
    }
    setValues(values.filter((item) => item !== optionValue));
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setValues(values.filter((value) => !filteredValues.includes(value)));
      return;
    }
    const merged = new Set([...values, ...filteredValues]);
    setValues([...merged]);
  };

  const showAiSparkle = Boolean(aiField && aiFilledFields.includes(aiField));

  return (
    <div className="form-kit__field form-kit__field--list-multi">
      <FieldLabelRow aiField={aiField} aiFilledFields={aiFilledFields}>
        {labelText}
      </FieldLabelRow>
      <div
        className={`form-kit__list-panel-bordered${searchable ? ' form-kit__list-panel-bordered--searchable' : ''}`}
      >
        {searchable ? (
          <div className="form-kit__search-wrap">
            <SearchIcon className="form-kit__search-icon" size={16} />
            <input
              type="search"
              className="form-kit__search form-kit__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              disabled={disabled}
              aria-label={searchPlaceholder}
            />
          </div>
        ) : null}

        <label className="form-kit__select-all">
          <input
            type="checkbox"
            className="form-kit__select-all-input"
            checked={allFilteredSelected}
            disabled={disabled || filtered.length === 0}
            onChange={toggleSelectAll}
          />
          <ListMultiCheckbox checked={allFilteredSelected} />
          <span>Select all</span>
        </label>

        <div className="form-kit__list" role="listbox" aria-label={labelText}>
          {filtered.map((option) => {
            const selected = values.includes(option.value);
            return (
              <label
                key={option.value}
                className={`form-kit__list-item${selected ? ' form-kit__list-item--selected' : ''}`}
              >
                <input
                  type="checkbox"
                  className="form-kit__list-item-input"
                  checked={selected}
                  disabled={disabled}
                  onChange={(e) => toggleOption(option.value, e.target.checked)}
                />
                <ListMultiCheckbox checked={selected} />
                <span className="form-kit__list-item__main">
                  <span className="form-kit__list-item__label">
                    {option.label}
                    {showAiSparkle ? (
                      <SparkleIcon className="form-kit__list-item__sparkle" size={18} />
                    ) : null}
                  </span>
                  {option.description ? (
                    <span className="form-kit__list-item__device">{option.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
